import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import CartButton from '../../../components/CartButton';

const ADMIN_PIN = '2025';

interface NavbarProps {
  scrolled?: boolean;
  onLogoClick?: () => void;
}

export default function Navbar({ onLogoClick }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [adminVisible, setAdminVisible] = useState(false);
  const [adminPulse, setAdminPulse] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinShake, setPinShake] = useState(false);
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pinInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Auto-focus PIN input when modal opens
  useEffect(() => {
    if (pinModalVisible) {
      setTimeout(() => pinInputRef.current?.focus(), 100);
    }
  }, [pinModalVisible]);

  // Auto-hide admin button after 8 seconds
  useEffect(() => {
    if (adminVisible) {
      setAdminPulse(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        setAdminVisible(false);
        setAdminPulse(false);
      }, 8000);
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [adminVisible]);

  const handleLogoClick = useCallback(() => {
    clickCountRef.current += 1;

    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

    if (clickCountRef.current >= 3) {
      clickCountRef.current = 0;
      // Show PIN modal instead of admin button directly
      setPinValue('');
      setPinError(false);
      setPinModalVisible(true);
      return;
    }

    clickTimerRef.current = setTimeout(() => {
      if (clickCountRef.current < 3) {
        if (onLogoClick) {
          onLogoClick();
        } else {
          navigate('/');
        }
      }
      clickCountRef.current = 0;
    }, 600);
  }, [onLogoClick, navigate]);

  const handlePinSubmit = useCallback(() => {
    if (pinValue === ADMIN_PIN) {
      setPinModalVisible(false);
      setPinValue('');
      setPinError(false);
      setAdminVisible(true);
    } else {
      setPinError(true);
      setPinShake(true);
      setPinValue('');
      setTimeout(() => setPinShake(false), 600);
    }
  }, [pinValue]);

  const handlePinKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePinSubmit();
    if (e.key === 'Escape') {
      setPinModalVisible(false);
      setPinValue('');
      setPinError(false);
    }
  }, [handlePinSubmit]);

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPinValue(val);
    setPinError(false);
    if (val.length === 4) {
      // Auto-submit when 4 digits entered
      setTimeout(() => {
        if (val === ADMIN_PIN) {
          setPinModalVisible(false);
          setPinValue('');
          setPinError(false);
          setAdminVisible(true);
        } else {
          setPinError(true);
          setPinShake(true);
          setPinValue('');
          setTimeout(() => setPinShake(false), 600);
        }
      }, 120);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-lg shadow-md py-3'
            : 'bg-transparent py-5'
        }`}
      >
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between">
            {/* Logo — triple-click to reveal admin */}
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-3 cursor-pointer group bg-transparent border-none outline-none relative"
              aria-label="House of FMR - Melbourne Event Hire & Bar Equipment"
            >
              <img
                src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
                alt="House of FMR"
                className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-110 group-hover:drop-shadow-[0_0_12px_rgba(212,175,55,0.7)]"
              />
              <div className="hidden sm:block">
                <p className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300 font-display text-xl tracking-wide">House of FMR</p>
                <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-medium">Flair • Mixology • Rentals</p>
              </div>
              <div className="absolute top-full left-0 mt-2 px-3 py-1.5 bg-stone-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Flair • Mixology • Rentals
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {[
                { id: 'about', label: 'About Us' },
                { id: 'portfolio', label: 'Our Work' },
                { id: 'equipment', label: 'Equipment' },
                { id: 'glassware', label: 'Glassware' },
                { id: 'testimonials', label: 'Reviews' },
                { id: 'faq', label: 'FAQ' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    scrolled
                      ? 'text-stone-600 hover:text-amber-600'
                      : 'text-white/90 hover:text-amber-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Link to="/shop" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isActive('/shop') ? 'text-amber-600 font-semibold' : scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'}`}>Shop</Link>
              <Link to="/services" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isActive('/services') ? 'text-amber-600 font-semibold' : scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'}`}>Services</Link>
              <Link to="/equipment-hire" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isActive('/equipment-hire') ? 'text-amber-600 font-semibold' : scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'}`}>Hire</Link>
              <Link to="/gallery" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isActive('/gallery') ? 'text-amber-600 font-semibold' : scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'}`}>Gallery</Link>
              <Link to="/contact" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isActive('/contact') ? 'text-amber-600 font-semibold' : scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'}`}>Contact</Link>
            </div>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-3">
              <CartButton />
              <Link to="/wishlist" className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer ${scrolled ? 'bg-stone-100 text-stone-700 hover:bg-stone-200' : 'bg-white/10 text-white border border-amber-300/20 hover:bg-white/20'}`} aria-label="Wishlist">
                <i className="ri-heart-line text-lg"></i>
              </Link>
              <a href="tel:0411164144" className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'}`}>
                <i className="ri-phone-line"></i>
                0411 164 144
              </a>
              <button
                onClick={() => scrollToSection('inquiry')}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-full hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/30 border border-amber-300/20"
              >
                Get Instant Quote
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <CartButton />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all ${scrolled ? 'bg-stone-100 text-stone-900' : 'bg-white/10 text-white border border-amber-300/20'}`}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 bg-white rounded-2xl p-5 shadow-2xl border border-amber-100">
              <div className="space-y-1">
                {[
                  { id: 'about', label: 'About Us', icon: 'ri-information-line' },
                  { id: 'portfolio', label: 'Our Work', icon: 'ri-gallery-line' },
                  { id: 'equipment', label: 'Equipment', icon: 'ri-tools-line' },
                  { id: 'glassware', label: 'Glassware', icon: 'ri-goblet-line' },
                  { id: 'testimonials', label: 'Reviews', icon: 'ri-star-line' },
                  { id: 'faq', label: 'FAQ', icon: 'ri-question-line' },
                ].map((item) => (
                  <button key={item.id} onClick={() => scrollToSection(item.id)} className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                    <i className={`${item.icon} text-base`}></i>
                    {item.label}
                  </button>
                ))}
                <div className="h-px bg-stone-200 my-2"></div>
                <Link to="/shop" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}><i className="ri-store-2-line text-base"></i>Shop Products</Link>
                <Link to="/services" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}><i className="ri-service-line text-base"></i>Services & Pricing</Link>
                <Link to="/equipment-hire" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}><i className="ri-calendar-check-line text-base"></i>Equipment Hire</Link>
                <Link to="/gallery" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}><i className="ri-image-line text-base"></i>Event Gallery</Link>
                <Link to="/wishlist" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}><i className="ri-heart-line text-base"></i>Wishlist</Link>
                <Link to="/contact" className="flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap" onClick={() => setMobileMenuOpen(false)}><i className="ri-mail-line text-base"></i>Contact Us</Link>
                <div className="h-px bg-stone-200 my-2"></div>
                <button onClick={() => scrollToSection('inquiry')} className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-stone-900 text-sm font-semibold rounded-lg hover:from-amber-300 hover:via-amber-400 hover:to-amber-500 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-amber-500/30">
                  <i className="ri-mail-send-line text-base"></i>
                  Get Instant Quote
                </button>
                <a href="tel:0411164144" className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-stone-100 text-stone-700 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line text-base"></i>
                  Call 0411 164 144
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* PIN Modal */}
      {pinModalVisible && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
          onClick={(e) => { if (e.target === e.currentTarget) { setPinModalVisible(false); setPinValue(''); setPinError(false); } }}
        >
          <div
            className={`relative w-80 rounded-2xl p-8 flex flex-col items-center gap-5 ${pinShake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}
            style={{
              background: 'linear-gradient(160deg, #1a1208 0%, #2d1f06 60%, #1a1208 100%)',
              border: '1px solid rgba(212,175,55,0.45)',
              boxShadow: '0 0 40px rgba(212,175,55,0.2), 0 20px 60px rgba(0,0,0,0.7)',
            }}
          >
            {/* Close */}
            <button
              onClick={() => { setPinModalVisible(false); setPinValue(''); setPinError(false); }}
              className="absolute top-3 right-3 w-7 h-7 rounded-full bg-stone-800 border border-amber-600/30 text-amber-400/70 flex items-center justify-center cursor-pointer hover:bg-stone-700 hover:text-amber-300 transition-colors"
            >
              <i className="ri-close-line text-sm"></i>
            </button>

            {/* Icon */}
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))', border: '1px solid rgba(212,175,55,0.35)' }}
            >
              <i className="ri-shield-keyhole-fill text-2xl text-amber-400"></i>
            </div>

            {/* Title */}
            <div className="text-center">
              <p className="text-amber-200 font-semibold text-base tracking-wide">Admin Access</p>
              <p className="text-amber-400/60 text-xs mt-1 tracking-wider">Enter your 4-digit PIN</p>
            </div>

            {/* PIN dots display */}
            <div className="flex items-center gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-full transition-all duration-200"
                  style={{
                    background: i < pinValue.length
                      ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                      : 'rgba(212,175,55,0.15)',
                    border: '1px solid rgba(212,175,55,0.4)',
                    boxShadow: i < pinValue.length ? '0 0 8px rgba(245,158,11,0.6)' : 'none',
                    transform: i < pinValue.length ? 'scale(1.15)' : 'scale(1)',
                  }}
                ></div>
              ))}
            </div>

            {/* Hidden input */}
            <input
              ref={pinInputRef}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pinValue}
              onChange={handlePinChange}
              onKeyDown={handlePinKeyDown}
              className="absolute opacity-0 w-0 h-0 pointer-events-none"
              aria-label="PIN input"
            />

            {/* Number pad */}
            <div className="grid grid-cols-3 gap-2.5 w-full">
              {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((key, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (key === '⌫') {
                      setPinValue(prev => prev.slice(0, -1));
                      setPinError(false);
                    } else if (key !== '' && pinValue.length < 4) {
                      const newVal = pinValue + String(key);
                      setPinValue(newVal);
                      setPinError(false);
                      if (newVal.length === 4) {
                        setTimeout(() => {
                          if (newVal === ADMIN_PIN) {
                            setPinModalVisible(false);
                            setPinValue('');
                            setPinError(false);
                            setAdminVisible(true);
                          } else {
                            setPinError(true);
                            setPinShake(true);
                            setPinValue('');
                            setTimeout(() => setPinShake(false), 600);
                          }
                        }, 120);
                      }
                    }
                  }}
                  disabled={key === ''}
                  className={`h-12 rounded-xl text-sm font-semibold transition-all duration-150 cursor-pointer whitespace-nowrap ${
                    key === ''
                      ? 'invisible'
                      : key === '⌫'
                      ? 'text-amber-400/70 hover:text-amber-300'
                      : 'text-amber-200 hover:text-amber-100'
                  }`}
                  style={key !== '' ? {
                    background: 'rgba(212,175,55,0.08)',
                    border: '1px solid rgba(212,175,55,0.2)',
                  } : {}}
                  onMouseEnter={(e) => {
                    if (key !== '') (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.18)';
                  }}
                  onMouseLeave={(e) => {
                    if (key !== '') (e.currentTarget as HTMLButtonElement).style.background = 'rgba(212,175,55,0.08)';
                  }}
                >
                  {key}
                </button>
              ))}
            </div>

            {/* Error message */}
            <div className={`text-xs font-medium transition-all duration-300 ${pinError ? 'text-red-400 opacity-100' : 'opacity-0'}`}>
              <i className="ri-error-warning-line mr-1"></i>
              Incorrect PIN. Please try again.
            </div>

            {/* Confirm button */}
            <button
              onClick={handlePinSubmit}
              disabled={pinValue.length < 4}
              className="w-full py-3 rounded-xl text-sm font-semibold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: pinValue.length === 4
                  ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                  : 'rgba(212,175,55,0.15)',
                color: pinValue.length === 4 ? '#1a1208' : 'rgba(212,175,55,0.5)',
                border: '1px solid rgba(212,175,55,0.3)',
              }}
            >
              Unlock Portal
            </button>
          </div>
        </div>
      )}

      {/* Secret Admin Floating Button */}
      <div
        className={`fixed bottom-8 right-8 z-[9999] transition-all duration-500 ${
          adminVisible
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <Link
          to="/fmr-portal/login"
          onClick={() => setAdminVisible(false)}
          className="flex items-center gap-2.5 px-5 py-3 rounded-full shadow-2xl cursor-pointer whitespace-nowrap group"
          style={{
            background: 'linear-gradient(135deg, #1a1208 0%, #2d1f06 50%, #1a1208 100%)',
            border: '1px solid rgba(212,175,55,0.5)',
            boxShadow: '0 0 24px rgba(212,175,55,0.35), 0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          <span
            className={`w-2 h-2 rounded-full bg-amber-400 ${adminPulse ? 'animate-pulse' : ''}`}
          ></span>
          <i className="ri-shield-keyhole-line text-amber-400 text-base"></i>
          <span className="text-amber-300 text-sm font-semibold tracking-wide">Admin Portal</span>
          <i className="ri-arrow-right-line text-amber-500 text-sm group-hover:translate-x-1 transition-transform"></i>
        </Link>
        {/* Dismiss button */}
        <button
          onClick={() => setAdminVisible(false)}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-stone-800 border border-amber-600/40 text-amber-400 flex items-center justify-center cursor-pointer hover:bg-stone-700 transition-colors"
          aria-label="Dismiss"
        >
          <i className="ri-close-line text-xs"></i>
        </button>
      </div>
    </>
  );
}
